import React, { useCallback, useMemo, useRef, useState } from 'react';

import { t } from 'i18next';
import { clone, get, pick } from 'lodash';
import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
  Box,
  Chip,
  ClickAwayListener,
  TextFieldProps,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { StyleProp, TextField } from 'components';
import { FieldInputProps, FieldProps } from 'formik';
import { IInputComponentParams } from 'components/input-fields/helpers/interfaces';
import { mergeUniqueStrings } from 'utils/arrays';
import { Empty, isBlank } from './common';

const styles = {
  chips: {
    '& > :not(:last-child)': { mr: 1 },
    transition: '2s',
  },
  chip: {
    marginBottom: 2,
    transition: '2s',
  },
};

const LeoAutoCompleteMulti = ({
  form,
  field,
  meta,
  options,
  accessor,
  maxLength,
  disabled = false,
  freeSolo = false,
  blankFilterOption = false,
  ...props
}: IInputComponentParams & TextFieldProps & FieldProps) => {
  const [autoFocus, setAutoFocus] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<React.RefObject<HTMLInputElement>>(null);

  const getValue: () => string[] = () => form.values[`${field.name}`] || [];

  const setUniqueValues = useCallback(
    (items: string[]) => {
      form.setFieldValue(`${field.name}`, mergeUniqueStrings(items));
    },
    [field.name, form]
  );

  const getData = useCallback(() => {
    const newData = clone(options);
    if (blankFilterOption && newData) {
      newData.unshift(Empty.VALUE);
    }

    return newData || [];
  }, [blankFilterOption, options]);

  const renderInput = useCallback(
    (params: FieldProps<any, any> | AutocompleteRenderInputParams) => {
      const fieldProps = pick(field, 'name', 'value') as FieldInputProps<any>;

      return (
        <TextField
          {...props}
          {...params}
          field={fieldProps}
          form={form}
          meta={meta}
          placeholder={t('COMMON.CHOOSE')}
          inputRef={inputRef}
          selectFieldHelper={true}
          showError
          autoFocus={autoFocus}
          extraInputProps={{ maxLength } as StyleProp}
        />
      );
    },
    [autoFocus, field, form, meta, props, maxLength]
  );

  const onClickAway = () => {
    setAutoFocus(false);
  };

  const onChange = (
    _event: React.SyntheticEvent<Element>,
    items: string[],
    _reason: AutocompleteChangeReason,
    details: AutocompleteChangeDetails<string> | undefined
  ) => {
    const hasBlank = items.find((item) => isBlank(item));

    const filteredItems = items.filter((item) => {
      if (hasBlank && details) {
        return isBlank(details.option) ? isBlank(item) : !isBlank(item);
      }
      return item.toString().trim();
    });

    setUniqueValues(filteredItems);
    setInputValue('');
  };

  const getOptionLabel = useCallback(
    (option: string) => {
      const newAccessor = clone(accessor);
      if (blankFilterOption && newAccessor) {
        Object.assign(newAccessor, { blank: t('COMMON.BLANK') });
      }
      return newAccessor ? get(newAccessor, option) || option || '' : option?.toString().trim();
    },
    [accessor, blankFilterOption]
  );

  const onDelete = (title: string) => () => {
    setUniqueValues(getValue().filter((item) => item !== title));
  };

  const getChipContent = () => (
    <Box sx={styles.chips}>
      {getValue()?.map((valueItem) => (
        <Chip
          sx={styles.chip}
          color="primary"
          key={valueItem}
          label={<Typography variant="subtitle2">{getOptionLabel(valueItem)}</Typography>}
          onDelete={onDelete(valueItem)}
        />
      ))}
    </Box>
  );

  const onInputChange = useCallback((_event: React.SyntheticEvent, value: string) => {
    setInputValue(value);
  }, []);

  const commonProps = useMemo(
    () => ({
      renderTags: () => null,
      options: getData(),
      getOptionLabel,
      renderInput,
      disabled,
      freeSolo,
      inputValue,
      onInputChange,
    }),
    [getData, renderInput, getOptionLabel, disabled, freeSolo, inputValue, onInputChange]
  );

  return (
    <>
      <ClickAwayListener onClickAway={onClickAway}>
        <Autocomplete
          multiple
          value={getValue()}
          onChange={onChange}
          popupIcon={<KeyboardArrowDownIcon />}
          forcePopupIcon
          {...commonProps}
        />
      </ClickAwayListener>
      {getChipContent()}
    </>
  );
};

export default LeoAutoCompleteMulti;
